import { useState, useEffect } from "react";
import { safeHref } from "../lib/safeUrl";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import TaxonomyManager from "../components/TaxonomyManager";
import AdminManageMenu from "../components/AdminManageMenu";
import AdminEditResourceModal from "../components/AdminEditResourceModal";
import { Link, useNavigate } from "react-router-dom";
import {
  GraduationCap, BookOpen, HelpCircle, Briefcase, Stethoscope,
  DollarSign, Users, Plus, ExternalLink, Heart, ArrowRight, Pencil
} from "lucide-react";

const CAT_ICONS = {
  tips: BookOpen, faq: HelpCircle, career: Briefcase,
  clinical: Stethoscope, financial: DollarSign, mentorship: Users
};
const CAT_COLORS = {
  tips: "#10B981", faq: "#3B82F6", career: "#8DB5A0",
  clinical: "#8DB5A0", financial: "#6B4E8A", mentorship: "#8B5CF6"
};

const NewGradPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: "", category: "tips", content: "", link: "" });
  const [editingResource, setEditingResource] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const isAdmin = user?.is_admin;

  useEffect(() => {
    fetchAll();
    axios.get(`${API}/new-grads/categories`).then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [resRes, discRes] = await Promise.all([
        axios.get(`${API}/new-grads/resources${selectedCat ? `?category=${selectedCat}` : ""}`),
        axios.get(`${API}/new-grads/discussions`)
      ]);
      setResources(resRes.data);
      setDiscussions(discRes.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [selectedCat]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) { toast.error("Fill in title and content"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/new-grads/resources`, form);
      toast.success("Resource added!");
      setShowAdd(false);
      setForm({ title: "", category: "tips", content: "", link: "" });
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try { await axios.delete(`${API}/new-grads/resources/${id}`); toast.success("Deleted"); fetchAll(); }
    catch { toast.error("Failed"); }
  };

  const grouped = categories.map(cat => ({
    ...cat,
    items: resources.filter(r => r.category === cat.key)
  })).filter(g => !selectedCat || g.key === selectedCat);

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="new-grad-page">
        {/* Disclaimer */}
        <div className="bg-[#F0ECF7]/70 border border-[#E5DFEC] rounded-xl p-3 mb-6 flex items-start gap-2" data-testid="newgrad-disclaimer">
          <GraduationCap className="w-4 h-4 text-[#6B4E8A] mt-0.5 shrink-0" />
          <p className="text-xs text-[#6B4E8A] leading-relaxed">
            Resources provided here are for general informational purposes only and do not replace formal clinical education or regulatory guidelines. Always refer to your provincial regulatory body for scope of practice guidance.
          </p>
        </div>

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-[#8DB5A0] to-[#8DB5A0] rounded-3xl p-8 lg:p-12 text-white mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
              <GraduationCap className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-medium inline-flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <EditableText blockKey="newgrad.title" fallback="New Grad Hub" as="span" />
                <TaxonomyManager
                  kind="newgrad_resource_categories"
                  title="New-grad resource categories"
                  description="Manage the category tags shown on /new-grads."
                  triggerLabel="Manage new-grad categories"
                />
              </h1>
              <EditableText
                blockKey="newgrad.subtitle"
                fallback="Resources, tips, and support for new dental hygienists"
                as="p"
                className="text-white/70 text-sm"
                multiline
              />
            </div>
          </div>
          <EditableText
            blockKey="newgrad.body"
            fallback="Welcome to the profession! Starting your career can feel overwhelming. This hub has everything you need — from clinical tips to financial advice, career guidance, and a community of experienced hygienists ready to support you."
            as="p"
            className="text-white/80 max-w-2xl"
            multiline
          />
        </motion.div>

        {/* Action bar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSelectedCat("")} className={`px-4 py-2 text-sm rounded-full border transition-colors ${!selectedCat ? 'bg-[#6B4E8A] text-white border-[#6B4E8A]' : 'border-[#E5DFEC] text-[#64748B] hover:border-[#6B4E8A]'}`}>All</button>
            {categories.map(c => {
              const Icon = CAT_ICONS[c.key] || BookOpen;
              return (
                <button key={c.key} onClick={() => setSelectedCat(c.key)} className={`px-4 py-2 text-sm rounded-full border transition-colors flex items-center gap-1.5 ${selectedCat === c.key ? 'bg-[#6B4E8A] text-white border-[#6B4E8A]' : 'border-[#E5DFEC] text-[#64748B] hover:border-[#6B4E8A]'}`}>
                  <Icon className="w-3.5 h-3.5" />{c.label}
                </button>
              );
            })}
          </div>
          {isAdmin && (
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
              <DialogTrigger asChild><Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="add-resource-btn"><Plus className="w-4 h-4 mr-2" /> Add Resource</Button></DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add New Grad Resource</DialogTitle></DialogHeader>
                <form onSubmit={handleAdd} className="space-y-4 mt-4">
                  <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="resource-title-input" /></div>
                  <div><Label>Category</Label>
                    <Select value={form.category} onValueChange={v => setForm({...form, category: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                      <SelectContent>{categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div><Label>Content *</Label><Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} className="mt-2 bg-white border-[#E5DFEC] min-h-[120px]" data-testid="resource-content-input" /></div>
                  <div><Label>Link (optional)</Label><Input value={form.link} onChange={e => setForm({...form, link: e.target.value})} placeholder="https://..." className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setShowAdd(false)} className="rounded-full">Cancel</Button><Button type="submit" className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" disabled={submitting} data-testid="submit-resource-btn">{submitting ? "Adding..." : "Add"}</Button></div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-28 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
        ) : (
          <>
            {/* Resources by Category */}
            {grouped.map(group => group.items.length > 0 && (
              <motion.div key={group.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  {(() => { const Icon = CAT_ICONS[group.key] || BookOpen; return <Icon className="w-5 h-5" style={{ color: CAT_COLORS[group.key] || "#64748B" }} />; })()}
                  <h2 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{group.label}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {group.items.map(item => {
                    const isExpanded = expandedId === item.resource_id;
                    const Icon = CAT_ICONS[group.key] || BookOpen;
                    const accent = CAT_COLORS[group.key] || "#64748B";
                    return (
                      <motion.div
                        key={item.resource_id}
                        layout
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`relative bg-white border border-[#E5DFEC] rounded-2xl p-5 group card-hover ${isExpanded ? "md:col-span-2 lg:col-span-3" : "aspect-square flex flex-col"}`}
                        data-testid={`newgrad-card-${item.resource_id}`}
                      >
                        {isAdmin && (
                          <div className="absolute top-3 right-3 z-10">
                            <AdminManageMenu
                              resourceType="newgrad-resource"
                              resourceId={item.resource_id}
                              resourceLabel={item.title}
                              onDeleted={fetchAll}
                              extraActions={[
                                {
                                  label: "Edit resource",
                                  icon: Pencil,
                                  onClick: () => setEditingResource(item),
                                },
                              ]}
                            />
                          </div>
                        )}
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 shrink-0" style={{ backgroundColor: `${accent}15` }}>
                          <Icon className="w-5 h-5" style={{ color: accent }} />
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : item.resource_id)}
                          className="block w-full text-left"
                          data-testid={`newgrad-toggle-${item.resource_id}`}
                        >
                          <h3 className="font-medium text-[#6B4E8A] hover:text-[#553D6F] mb-2 line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {item.title}
                          </h3>
                        </button>
                        {isExpanded ? (
                          <div className="flex-1" data-testid={`newgrad-expanded-${item.resource_id}`}>
                            <p className="text-sm text-[#475569] whitespace-pre-wrap leading-relaxed mb-3">
                              {item.content}
                            </p>
                            {item.link && (
                              <a href={safeHref(item.link)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#8DB5A0] hover:text-[#D0694E]">
                                <ExternalLink className="w-3.5 h-3.5" /> Visit external link
                              </a>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-[#64748B] line-clamp-3 flex-1">
                            {item.content}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : item.resource_id)}
                          className="text-xs text-[#6B4E8A] hover:text-[#553D6F] font-medium mt-2 text-left"
                          data-testid={`newgrad-cta-${item.resource_id}`}
                        >
                          {isExpanded ? "Show less ↑" : "Read more →"}
                        </button>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}

            {resources.length === 0 && (
              <div className="text-center py-12 bg-white border border-[#E5DFEC] rounded-2xl mb-8">
                <GraduationCap className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Resources coming soon</h3>
                <p className="text-[#64748B]">Check back for new grad tips, career guidance, and more</p>
              </div>
            )}

            {/* Related Discussions */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-[#E5DFEC] rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>New Grad Discussions</h3>
                <Link to="/discussions?tag=new-grad" className="text-sm text-[#8DB5A0] hover:text-[#D0694E] flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
              </div>
              {discussions.length > 0 ? (
                <div className="space-y-2">
                  {discussions.map(d => (
                    <Link key={d.discussion_id} to={`/discussions/${d.discussion_id}`} className="block p-3 bg-[#FAF7F2] rounded-xl hover:bg-[#F0ECF7] transition-colors">
                      <p className="font-medium text-sm text-[#6B4E8A]">{d.title}</p>
                      <p className="text-xs text-[#64748B] mt-0.5">{d.author_name} · {d.replies_count} replies</p>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-[#64748B]">No discussions tagged "new-grad" yet. <Link to="/discussions" className="text-[#8DB5A0]">Start one!</Link></p>
                </div>
              )}
            </motion.div>

            {/* Mentorship Teaser */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-8 bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 rounded-2xl p-6 text-center">
              <Users className="w-10 h-10 mx-auto mb-3 text-[#8B5CF6]" />
              <h3 className="font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Mentorship Program</h3>
              <p className="text-sm text-[#64748B] max-w-md mx-auto">Coming soon — get matched with experienced RDHs who can guide your career journey. Stay tuned!</p>
            </motion.div>
          </>
        )}
      </div>
      <AdminEditResourceModal
        resource={editingResource}
        resourceType="newgrad-resource"
        open={!!editingResource}
        onClose={() => setEditingResource(null)}
        onSaved={() => fetchAll()}
      />
    </Layout>
  );
};

export default NewGradPage;
